import type { RouteStop } from '@/lib/reseller-api';

export interface TrackPoint {
  latitude: number;
  longitude: number;
  fixTime: string;
}

export interface StopVisit {
  arrivedAt: string | null;
  departedAt: string | null;
}

export const distanceMeters = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Matches the vehicle's track against each stop independently (drivers often visit
 * stops out of the planned order): a stop is visited at the first point inside its
 * radius, and departed at the first point after that outside it.
 */
export const computeStopVisits = (stops: RouteStop[], track: TrackPoint[]): StopVisit[] => {
  const points = [...track].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
  return stops.map((stop) => {
    const inside = (point: TrackPoint) =>
      distanceMeters(point.latitude, point.longitude, stop.latitude, stop.longitude) <= stop.radius;
    const arrival = points.findIndex(inside);
    if (arrival < 0) return { arrivedAt: null, departedAt: null };
    const departure = points.findIndex((point, index) => index > arrival && !inside(point));
    return {
      arrivedAt: points[arrival].fixTime,
      departedAt: departure >= 0 ? points[departure].fixTime : null,
    };
  });
};
